import { t } from "i18next";
import { ChevronDown, Loader2, User, Users } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type { JSX } from "react";
import { useState } from "react";
import { AppConfig } from "@/common/appConfig";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/Combobox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/Toggle/toggle-group";
import { TooltipHelp } from "@/components/ui/TooltipHelp";
import { NoiseReduction } from "@/hooks/transcriber/types";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "../select";
import { NoteLanguageSelect } from "./NoteLanguageSelect";
import { TranscriptionProvidersSelector } from "./TranscriptionProviderSelector";
import { useRecordingSetup } from "./useRecordingSetup";

export const AdvancedSettings = (): JSX.Element => {
  const [open, setOpen] = useState(false);
  const {
    inputLanguageOptions,
    inputLanguageValue,
    setInputLanguage,
    mics,
    loadingMics,
    mobileDevices,
    loadingDevices,
    deviceError,
    refreshDevices,
    micDevice,
    setMicDevice: handleMicDeviceChange,
    noiseReduction,
    setNoiseReduction: handleNoiseReductionChange,
    noiseSuppression,
    setNoiseSuppression: saveNoiseSuppression,
    isRealtimeEnabled,
    isProductionEnv,
    transcriptionProvider: {
      value: transcriptionProviderValue,
      values: transcriptionProviderValues,
      handleChange: transcriptionProviderHandleChange,
    },
  } = useRecordingSetup();

  return (
    <div className="space-y-4">
      {/* Input Language */}
      <div className="flex flex-col gap-2">
        <div>
          <Label className="text-tinted-grey-3">
            {t("components.advancedSettings.selectLanguage.label")}
          </Label>
          <div className="flex items-center gap-4">
            <div className="w-full">
              <Combobox
                items={inputLanguageOptions}
                value={inputLanguageValue}
                placeholder={t(
                  "components.advancedSettings.selectLanguage.placeholder",
                )}
                onValueChange={(value) => {
                  setInputLanguage(value.value);
                }}
              />
            </div>
            <TooltipHelp
              content={t("components.advancedSettings.selectLanguage.tooltip")}
            />
          </div>
        </div>
        <NoteLanguageSelect inputLanguage={inputLanguageValue} />
        <div>
          <Label className="text-tinted-grey-3 text-sm">
            {t("components.advancedSettings.microphoneDevice.label")}
          </Label>
          <div className="flex items-center gap-4">
            <Select
              disabled={loadingMics}
              value={micDevice?.value}
              onValueChange={handleMicDeviceChange}
            >
              <SelectTrigger className="focus:ring-0">
                <SelectValue
                  placeholder={t(
                    "components.advancedSettings.microphoneDevice.placeholder",
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {mics.map((mic) => (
                  <SelectItem key={mic.value} value={mic.value}>
                    {mic.label}
                  </SelectItem>
                ))}
                <div hidden={!isRealtimeEnabled}>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel>
                      {t(
                        "components.advancedSettings.microphoneDevice.mobileGroupLabel",
                      )}
                    </SelectLabel>
                    {loadingDevices ? (
                      <div
                        role="status"
                        aria-label={t(
                          "components.advancedSettings.microphoneDevice.loading",
                          "Loading mobile devices",
                        )}
                        className="flex items-center justify-center"
                      >
                        <Loader2 className="animate-spin" />
                      </div>
                    ) : deviceError ? (
                      <div role="alert" className="p-2">
                        <p>{t("error.unknown")}</p>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            void refreshDevices();
                          }}
                        >
                          {t("common.retry", "Retry")}
                        </Button>
                      </div>
                    ) : (
                      mobileDevices.map((element) => (
                        <SelectItem key={element.value} value={element.value}>
                          {element.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectGroup>
                </div>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      {/* Advanced Settings */}
      <div hidden={!AppConfig.enableAdvancedSettings}>
        <div className="flex items-center justify-between">
          <Label className="text-tinted-grey-3 text-sm">
            {t("components.advancedSettings.advancedSettings")}
          </Label>
          <button
            type="button"
            className="text-sm text-primary flex items-center hover:underline transition-colors"
            onClick={() => {
              setOpen((previous) => !previous);
            }}
          >
            {open
              ? t("components.advancedSettings.toggle.hide")
              : t("components.advancedSettings.toggle.show")}
            <ChevronDown
              className={`ml-1 h-4 w-4 transition-transform duration-300 ${
                open ? "rotate-180" : "rotate-0"
              }`}
            />
          </button>
        </div>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="advanced-settings"
              animate={{ height: "auto", opacity: 1 }}
              className="overflow-hidden"
              exit={{ height: 0, opacity: 0 }}
              initial={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <div className="space-y-2 mt-2">
                <div className="flex items-center gap-4">
                  <div className="w-full">
                    <Label className="text-tinted-grey-3 text-sm">
                      {t(
                        "components.advancedSettings.optionNoiseReduction.label",
                      )}
                    </Label>
                    <div className="flex items-center gap-4">
                      <ToggleGroup
                        className="w-full"
                        type="single"
                        value={noiseReduction}
                        onValueChange={handleNoiseReductionChange}
                      >
                        <ToggleGroupItem
                          className="flex-1"
                          value={NoiseReduction.FAR_FIELD}
                        >
                          <Users className="mr-2 h-4 w-4" />
                          {t(
                            "components.advancedSettings.optionNoiseReduction.farOption",
                          )}
                        </ToggleGroupItem>
                        <ToggleGroupItem
                          className="flex-1"
                          value={NoiseReduction.NEAR_FIELD}
                        >
                          <User className="mr-2 h-4 w-4" />
                          {t(
                            "components.advancedSettings.optionNoiseReduction.nearOption",
                          )}
                        </ToggleGroupItem>
                      </ToggleGroup>
                      <TooltipHelp
                        content={t(
                          "components.advancedSettings.optionNoiseReduction.tooltip",
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div className="w-full">
                  <div className="flex items-center justify-end gap-2">
                    <Label className="text-tinted-grey-3 text-sm">
                      {t(
                        "components.advancedSettings.optionNoiseSuppression.label",
                      )}
                    </Label>
                    <Switch
                      checked={noiseSuppression}
                      onCheckedChange={(checked) => {
                        saveNoiseSuppression(checked);
                      }}
                    />
                    <TooltipHelp
                      content={t(
                        "components.advancedSettings.optionNoiseSuppression.tooltip",
                      )}
                    />
                  </div>
                </div>
                {/* Not in the v3.2 app: the provider is chosen on the
                    server (server-side STT routing). */}
                {!isProductionEnv && !AppConfig.enableAppRedesign && (
                  <TranscriptionProvidersSelector
                    value={transcriptionProviderValue}
                    values={transcriptionProviderValues}
                    onChange={transcriptionProviderHandleChange}
                  />
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
